export const content = {
  en: {
    meta: {
      brand: 'EMFA',
      brandSuffix: 'IT Consulting',
      tagline: 'Möckmühl · Est. 2020',
    },
    nav: {
      home: 'Home',
      services: 'Services',
      process: 'Process',
      stack: 'Stack',
      work: 'Industries',
      pricing: 'Engagement',
      faq: 'FAQ',
      contact: 'Contact',
      consult: 'Book a call',
      menu: 'Menu',
      close: 'Close',
      switchTheme: 'Toggle theme',
      switchLang: 'Switch language',
      skipToContent: 'Skip to content',
    },
    hero: {
      eyebrow: 'Enterprise IT, built deliberately',
      title: 'Your partner for tailor‑made IT solutions.',
      titleAccent: 'Strategy, infrastructure, software.',
      subtitle:
        'In a market that resets weekly, the teams that win are the ones whose IT keeps up. We design, build and operate infrastructure and software that earns its budget — measured in uptime, velocity and cost saved.',
      cta: 'Get a free consultation',
      ctaSecondary: 'See how we work',
      stats: [
        { value: '25+', label: 'Projects delivered' },
        { value: '99.9%', label: 'Avg. uptime SLA' },
        { value: '24/7', label: 'Managed support' },
        { value: '10+', label: 'Years in business' },
      ],
      ticker: 'Trusted by SMBs across DACH · GDPR-native · ISO‑aligned',
    },
    trust: {
      title: 'Operating across industries',
      items: ['Manufacturing', 'Healthcare', 'Logistics', 'Retail', 'Professional services', 'Public sector', 'SaaS'],
    },
    services: {
      eyebrow: 'What we do',
      title: 'Four practices, one accountable team.',
      subtitle:
        'We sit at the intersection of strategy, infrastructure and software. Every engagement begins with a single accountable lead.',
      consulting: {
        title: 'IT Consulting',
        short: 'From strategy to a board-ready roadmap.',
        description:
          'Customized IT consulting that translates business goals into a concrete, sequenced technology plan — and a budget you can defend.',
        features: [
          'Strategic consulting: business goals translated into a future-proof IT strategy.',
          'Efficiency optimization: streamlined processes using current technologies.',
          'IT security: protecting your data and systems from cyber threats.',
          'Cloud & digitalization: clear migration paths and adoption plans.',
        ],
        outcomes: ['12-week roadmap', 'TCO model', 'Risk register'],
      },
      windowsServer: {
        title: 'Windows Server',
        short: 'Maximize performance, security and stability.',
        description:
          'Run a modern Windows Server estate that is fast, secure and observable — on‑prem, hybrid or fully cloud.',
        features: [
          'Installation & setup — tailored configuration for your needs.',
          'Migration & updates — seamless upgrades to the latest server versions.',
          'Monitoring & maintenance — proactive monitoring for maximum uptime.',
          'Security management — modern protection against cyber threats.',
          'Backup & recovery — reliable data protection solutions.',
          'Virtualization — Hyper‑V and modern virtualization.',
        ],
        outcomes: ['Zero‑downtime migration', '15‑min RTO', 'Hardened baseline'],
      },
      webDevelopment: {
        title: 'Web Development',
        short: 'Custom websites and apps that convert.',
        description:
          'Custom websites that inspire — your vision, perfectly executed. Production-grade React, performant by default, accessible by design.',
        features: [
          'Custom web development — unique websites that reflect your brand.',
          'Responsive design — perfect display on all devices.',
          'SEO optimization — better visibility on search engines.',
          'E‑commerce solutions — modern online shops.',
          'CMS integration — easy content management.',
          'Performance — Core Web Vitals in the green.',
        ],
        outcomes: ['100 Lighthouse', 'WCAG 2.2 AA', 'Sub‑2s LCP'],
      },
      windowsOS: {
        title: 'Windows OS Services',
        short: 'Endpoint support that actually works.',
        description:
          'Your Windows operating system service — support that works. Imaging, optimization, hardening and end‑user enablement at any scale.',
        features: [
          'Installation & setup — complete installation of all Windows versions.',
          'Optimization & performance tuning — faster startup and system performance.',
          'Error diagnosis & repair — troubleshooting system issues.',
          'Security & privacy — protection against cyber threats.',
          'Training & support — user-friendly training and assistance.',
        ],
        outcomes: ['First‑call resolution', 'Golden image', 'User training'],
      },
    },
    process: {
      eyebrow: 'How we work',
      title: 'A predictable process, transparent at every step.',
      subtitle: 'Fixed weekly rituals, written artifacts, no surprises at invoicing.',
      steps: [
        { n: '01', title: 'Discover', text: 'Two-week diagnostic with stakeholder interviews, a current-state map and a costed risk register.' },
        { n: '02', title: 'Design',   text: 'Architecture diagrams, success metrics and a sequenced plan you sign off on before anything ships.' },
        { n: '03', title: 'Deliver',  text: 'Weekly demos against a written backlog. Code, configs and docs in your repos from day one.' },
        { n: '04', title: 'Operate',  text: 'Optional managed support: monitored SLAs, on-call rotation, monthly health reports.' },
      ],
    },
    stack: {
      eyebrow: 'Tech we run in production',
      title: 'Modern by default, boring where it counts.',
      subtitle:
        'We prefer proven tools and tight feedback loops. Everything we recommend, we run ourselves.',
      groups: [
        { label: 'Cloud & infra', items: ['Azure', 'AWS', 'Hyper-V', 'VMware', 'Active Directory', 'Entra ID', 'Terraform'] },
        { label: 'Server & ops',  items: ['Windows Server 2022', 'PowerShell', 'Veeam', 'PRTG', 'Zabbix', 'Grafana'] },
        { label: 'Web & apps',    items: ['React', 'TypeScript', 'Node.js', '.NET', 'PostgreSQL', 'Next.js', 'Tailwind'] },
        { label: 'Security',      items: ['Microsoft Defender', 'CrowdStrike', 'KeePass', 'Wazuh', 'ISO 27001 controls'] },
      ],
    },
    stats: {
      eyebrow: 'By the numbers',
      title: 'Results we hold ourselves to.',
      items: [
        { value: 25, suffix: '+',  label: 'Projects delivered',     hint: 'Across DACH SMBs and mid-market' },
        { value: 99.9, suffix: '%', label: 'Average uptime',        hint: 'Measured across managed estates' },
        { value: 24,   suffix: '/7', label: 'Managed support',      hint: 'Tiered response, on‑call rotation' },
        { value: 10,  suffix: '+', label: 'Years in business',       hint: 'Serving DACH clients since day one' },
      ],
    },
    work: {
      eyebrow: 'Industries we serve',
      title: 'Different domains. The same engineering discipline.',
      industries: [
        { name: 'Manufacturing',         text: 'OT/IT convergence, MES integrations, hardened shop‑floor endpoints.' },
        { name: 'Healthcare',            text: 'GDPR-aligned record systems, secure remote access, audit-ready logging.' },
        { name: 'Logistics',             text: 'WMS/ERP integration, scanner fleets, warehouse Wi‑Fi.' },
        { name: 'Professional services', text: 'Identity, MDM, Microsoft 365 governance and joiner-mover-leaver.' },
        { name: 'Retail',                text: 'POS reliability, store networking, e‑commerce front‑ends.' },
        { name: 'Public sector',         text: 'Procurement-friendly contracts, documentation, on‑prem hosting.' },
      ],
    },
    testimonials: {
      eyebrow: 'In their own words',
      title: 'Clients keep us on retainer.',
      items: [],
    },
    pricing: {
      eyebrow: 'Engagement models',
      title: 'Pick the way of working that fits.',
      subtitle: 'All tiers include a named lead, weekly demos and written artifacts.',
      tiers: [
        {
          name: 'Diagnostic',
          tagline: 'Two-week scoped audit',
          price: 'Fixed fee',
          features: [
            'Stakeholder interviews',
            'Current-state architecture map',
            'Costed risk register',
            'Sequenced 90-day plan',
          ],
          cta: 'Start a diagnostic',
        },
        {
          name: 'Project',
          tagline: 'Defined outcome, fixed timeline',
          price: 'Get a quote',
          highlight: true,
          features: [
            'Named technical lead',
            'Weekly demos & written backlog',
            'Documentation in your repo',
            'Two-week post-launch warranty',
          ],
          cta: 'Scope a project',
        },
        {
          name: 'Managed',
          tagline: 'Ongoing operations & support',
          price: 'Retainer',
          features: [
            'Monitored SLAs (24/7 or business hours)',
            'On-call rotation',
            'Monthly health report',
            'Quarterly business review',
          ],
          cta: 'Talk about a retainer',
        },
      ],
    },
    faq: {
      eyebrow: 'Asked often',
      title: 'Questions before we talk.',
      items: [
        { q: 'How quickly can you start?',
          a: 'Diagnostic engagements typically begin within two weeks of contract. For projects, we line up named leads in the same window.' },
        { q: 'Do you work on-site, remote, or hybrid?',
          a: 'All three. We default to remote-first with on-site days at kickoff, key milestones and cutover. For local clients we are happy to be on the ground weekly.' },
        { q: 'Who owns the code and documentation?',
          a: 'You do. Everything ships into your repositories and wikis. We never hold infrastructure or IP hostage at the end of an engagement.' },
        { q: 'Are you GDPR compliant?',
          a: 'Yes. We are a Germany-based controller, default to EU data residency, and align our internal controls to ISO 27001.' },
        { q: 'How are you priced?',
          a: 'Diagnostics are a fixed fee. Projects are fixed-price against a written statement of work. Managed services are a transparent monthly retainer.' },
      ],
    },
    contact: {
      eyebrow: 'Talk to us',
      title: "Let's build your digital success.",
      subtitle:
        'Tell us what you are trying to do. A senior consultant — not a salesperson — responds inside one business day.',
      form: {
        name: 'Your name',
        email: 'Work email',
        company: 'Company',
        service: 'How can we help?',
        message: 'A few lines about what you need',
        submit: 'Send message',
        sending: 'Sending…',
        success: 'Thank you. We will be in touch within one business day.',
        errorRequired: 'Please complete the required fields.',
        errorEmail: 'Please enter a valid work email.',
        agreement: 'By sending, you agree to our data protection terms. We never share your details.',
      },
      info: [
        { label: 'Email',   value: 'info@emfa-it.de',      href: 'mailto:info@emfa-it.de' },
        { label: 'Phone',   value: '+49 171 5592218',                href: 'tel:+491715592218' },
        { label: 'Office',  value: 'Möckmühl', href: null },
        { label: 'Hours',   value: 'Mon–Fri · 09:00–18:00 CET',      href: null },
      ],
    },
    cta: {
      title: 'Stop maintaining your IT. Start compounding it.',
      subtitle: 'One conversation is usually enough to know if we are the right fit.',
      primary: 'Book a 30-minute call',
      secondary: 'Email us directly',
    },
    footer: {
      tagline: 'Enterprise IT, built deliberately. Möckmühl.',
      columns: {
        services: 'Services',
        company:  'Company',
        legal:    'Legal',
      },
      links: {
        services: 'Services',
        process:  'Process',
        work:     'Industries',
        pricing:  'Engagement',
        faq:      'FAQ',
        contact:  'Contact',
        dataProtection: 'Data Protection',
        imprint: 'Imprint',
      },
      rights: 'All rights reserved.',
      built: 'Built deliberately.',
      dataProtection: 'Data Protection',
      imprint: 'Imprint',
    },
    legal: {
      dataProtection: `Data Protection Declaration

1. An overview of data protection
The following information will provide you with an easy to navigate overview of what will happen with your personal data when you visit this website.

Who is responsible for the data recording on this website?
The data processing on this website is carried out by the website operator.

How do we record your data?
We collect your data as a result of your sharing of your data with us.

What do we use your data for?
Part of the data is collected to ensure the proper functioning of the website. Other data can be used to analyze how visitors use the site.

What rights do you have regarding your data?
You have the right to receive information about the source, recipients, and purposes of your archived personal data at any time without having to pay a fee for such disclosures. You also have the right to demand that your data are rectified or eradicated.`,
      imprint: `Imprint

Abdullah Gül
EMFA IT Consulting
Buchenweg, 15
74219 Möckmühl

Contact:
Email: info@emfa-it.de

Consumer Dispute Resolution/Universal Arbitration Board
We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.`,
    },
  },

  de: {
    meta: {
      brand: 'EMFA',
      brandSuffix: 'IT Consulting',
      tagline: 'Möckmühl · Gegründet 2020',
    },
    nav: {
      home: 'Start',
      services: 'Leistungen',
      process: 'Vorgehen',
      stack: 'Stack',
      work: 'Branchen',
      pricing: 'Modelle',
      faq: 'FAQ',
      contact: 'Kontakt',
      consult: 'Termin buchen',
      menu: 'Menü',
      close: 'Schließen',
      switchTheme: 'Theme wechseln',
      switchLang: 'Sprache wechseln',
      skipToContent: 'Zum Inhalt springen',
    },
    hero: {
      eyebrow: 'IT für Unternehmen — mit System',
      title: 'Wir machen IT planbar.',
      titleAccent: 'Strategie, Infrastruktur, Software.',
      subtitle:
        'Wir planen, implementieren und betreiben IT-Lösungen, die zuverlässig funktionieren. Von der Strategie bis zum laufenden Betrieb – sicher, skalierbar und auf die Anforderungen Ihres Unternehmens abgestimmt.',
      cta: 'Kostenloses Strategiegespräch',
      ctaSecondary: 'So arbeiten wir',
      stats: [
        { value: '25+', label: 'Projekte umgesetzt' },
        { value: '99,9%', label: 'Ø Verfügbarkeit SLA' },
        { value: '24/7', label: 'Managed Support' },
        { value: '10+', label: 'Jahre am Markt' },
      ],
      ticker: 'Vertrauenspartner für KMU im DACH-Raum · DSGVO-nativ · ISO-orientiert',
    },
    trust: {
      title: 'Branchenübergreifend im Einsatz',
      items: ['Fertigung', 'Gesundheit', 'Logistik', 'Handel', 'Dienstleistung', 'Öffentlicher Sektor', 'SaaS'],
    },
    services: {
      eyebrow: 'Unsere Leistungen',
      title: 'Vier Bereiche, ein verantwortliches Team.',
      subtitle:
        'Wir verbinden Strategie, Infrastruktur und Software. Jedes Projekt wird von einem festen Ansprechpartner verantwortet.',
      consulting: {
        title: 'IT‑Beratung',
        short: 'Modern Workplace',
        description:
          'Maßgeschneiderte IT-Beratung, die Geschäftsziele in konkrete Maßnahmen und eine umsetzbare IT-Strategie übersetzt.',
        features: [
          'Strategische Beratung: Geschäftsziele übersetzt in eine zukunftssichere IT‑Strategie.',
          'Effizienzoptimierung: Prozesse mit aktuellen Technologien verschlanken.',
          'IT‑Sicherheit: Schutz Ihrer Daten und Systeme vor Cyber‑Bedrohungen.',
          'Cloud & Digitalisierung: klare Migrationspfade und Einführungspläne.',
        ],
        outcomes: ['12‑Wochen‑Roadmap', 'TCO‑Modell', 'Risiko‑Register'],
      },
      windowsServer: {
        title: 'Windows Server',
        short: 'Maximale Leistung, Sicherheit und Stabilität.',
        description:
          'Moderne Windows Server-Infrastrukturen – schnell, sicher und zuverlässig überwacht.',
        features: [
          'Installation & Einrichtung — maßgeschneiderte Konfiguration.',
          'Migration & Updates — nahtlose Upgrades auf aktuelle Versionen.',
          'Monitoring & Wartung — proaktive Überwachung für maximale Betriebszeit.',
          'Sicherheitsmanagement — moderner Schutz vor Cyber‑Bedrohungen.',
          'Backup & Wiederherstellung — zuverlässige Datenschutzlösungen.',
          'Virtualisierung — Hyper‑V und moderne Virtualisierung.',
        ],
        outcomes: ['Null‑Downtime‑Migration', '15‑Min RTO', 'Security Baseline'],
      },
      webDevelopment: {
        title: 'Webentwicklung',
        short: 'Individuelle Websites & Apps, die konvertieren.',
        description:
          'Moderne Websites und Anwendungen, die Nutzer begeistern und messbare Ergebnisse liefern.',
        features: [
          'Individuelle Webentwicklung — Websites, die Ihre Marke widerspiegeln.',
          'Responsives Design — perfekte Darstellung auf allen Geräten.',
          'SEO‑Optimierung — bessere Sichtbarkeit in Suchmaschinen.',
          'E‑Commerce‑Lösungen — moderne Online‑Shops.',
          'CMS‑Integration — einfache Inhaltsverwaltung.',
          'Performance — Core Web Vitals im grünen Bereich.',
        ],
        outcomes: ['100 Lighthouse', 'WCAG 2.2 AA', 'LCP unter 2s'],
      },
      windowsOS: {
        title: 'Modern Workplace',
        short: 'Produktive Arbeitsplätze für moderne Unternehmen.',
        description:
          'Wir betreuen und optimieren Windows-Arbeitsplätze – von der Bereitstellung neuer Geräte über Sicherheitsrichtlinien bis hin zum laufenden Support.',
        features: [
          'Installation & Einrichtung — komplette Installation aller Windows‑Versionen.',
          'Optimierung & Performance‑Tuning — schnellerer Start und Systemleistung.',
          'Fehlerdiagnose & Reparatur — Fehlerbehebung bei Systemproblemen.',
          'Sicherheit & Datenschutz — Schutz vor Cyber‑Bedrohungen.',
          'Schulung & Support — benutzerfreundliche Schulungen.',
        ],
        outcomes: ['Automatisierte Bereitstellung', 'Einheitliche Sicherheitsstandards', 'Schnelle Einarbeitung'],
      },
    },
    process: {
      eyebrow: 'So arbeiten wir',
      title: 'Ein planbarer Prozess. Transparenz in jedem Schritt.',
      subtitle: 'Klare Prozesse, nachvollziehbare Ergebnisse und volle Transparenz bei Aufwand und Kosten.',
      steps: [
        { n: '01', title: 'Diagnose', text: 'Zweiwöchige Bestandsaufnahme mit Stakeholder-Interviews, Architekturübersicht und priorisiertem Risikoregister.' },
        { n: '02', title: 'Design',   text: 'Architekturdiagramme, messbare Erfolgsziele und ein klar strukturierter Umsetzungsplan.' },
        { n: '03', title: 'Lieferung', text: 'Wöchentliche Fortschritts-Demos, ein transparentes Backlog sowie Code und Dokumentation ab dem ersten Tag in Ihren Repositories.' },
        { n: '04', title: 'Betrieb',  text: 'Optionaler Managed Support mit SLA-Überwachung, Rufbereitschaft und monatlichem Statusbericht.' },
      ],
    },
    stack: {
      eyebrow: 'Im Produktivbetrieb',
      title: 'Modern, wo es sinnvoll ist. Bewährt, wo es zählt.',
      subtitle: 'Wir bevorzugen bewährte Werkzeuge und enge Feedback‑Schleifen. Wir setzen ein, was wir selbst betreiben.',
      groups: [
        { label: 'Cloud & Infrastruktur', items: ['Azure', 'AWS', 'Hyper-V', 'VMware', 'Active Directory', 'Entra ID', 'Terraform'] },
        { label: 'Server & Betrieb',      items: ['Windows Server', 'PowerShell', 'Veeam', 'PRTG', 'Zabbix', 'Grafana'] },
        { label: 'Web & Apps',            items: ['React', 'TypeScript', 'Node.js', '.NET', 'PostgreSQL', 'Next.js', 'Tailwind'] },
        { label: 'Sicherheit',            items: ['Microsoft Defender', 'CrowdStrike', 'Schwachstellenmanagement', 'Wazuh', 'ISO‑27001‑Kontrollen'] },
      ],
    },
    stats: {
      eyebrow: 'In Zahlen',
      title: 'Ergebnisse, an denen wir uns messen lassen.',
      items: [
        { value: 25,   suffix: '+',  label: 'Projekte erfolgreich umgesetzt', hint: 'Für Unternehmen im DACH-Raum' },
        { value: 99.9, suffix: '%',  label: 'Ø Verfügbarkeit',               hint: 'Über alle betreuten Systeme' },
        { value: 24,   suffix: '/7', label: 'Managed Support',               hint: 'Rund um die Uhr erreichbar' },
        { value: 10,   suffix: '+',  label: 'Jahre am Markt',          hint: 'Seit dem ersten Tag für DACH-Kunden' },
      ],
    },
    work: {
      eyebrow: 'Branchen',
      title: 'Branchenübergreifend im Einsatz.',
      subtitle: 'Bewährte Lösungen für Unternehmen mit unterschiedlichen Anforderungen – vom Mittelstand bis zur öffentlichen Verwaltung.',
      industries: [
        { name: 'Fertigung',           text: 'OT/IT‑Konvergenz, MES‑Integration, gehärtete Endgeräte am Shopfloor.' },
        { name: 'Gesundheit',          text: 'DSGVO‑konforme Aktensysteme, sicherer Fernzugriff, audit‑fähiges Logging.' },
        { name: 'Logistik',            text: 'WMS/ERP‑Integration, Scanner‑Flotten, Lager‑WLAN.' },
        { name: 'Dienstleistung',      text: 'Identity, MDM, Microsoft 365 Governance, Joiner‑Mover‑Leaver.' },
        { name: 'Handel',              text: 'POS‑Verfügbarkeit, Filialnetz, E‑Commerce Frontends.' },
        { name: 'Öffentlicher Sektor', text: 'Vergabe‑freundliche Verträge, Dokumentation, On‑Prem‑Hosting.' },
      ],
    },
    testimonials: {
      eyebrow: 'In ihren Worten',
      title: 'Kunden bleiben uns langfristig verbunden.',
      items: [],
    },
    pricing: {
      eyebrow: 'Engagement‑Modelle',
      title: 'Wählen Sie das passende Modell.',
      subtitle: 'Alle Modelle beinhalten einen festen Ansprechpartner, regelmäßige Abstimmungen und eine transparente Dokumentation.',
      tiers: [
        {
          name: 'Diagnose',
          tagline: 'Zweiwöchiges Audit',
          price: 'Festpreis',
          features: ['Stakeholder‑Interviews', 'Ist‑Architektur', 'Bewertetes Risiko‑Register', 'Maßnahmeplan'],
          cta: 'Diagnose starten',
        },
        {
          name: 'Projekt',
          tagline: 'Definiertes Ergebnis, fester Zeitplan',
          price: 'Individuelles Angebot',
          highlight: true,
          features: ['Fester technischer Ansprechpartner', 'Wöchentliche Demos & Backlog', 'Dokumentation in Ihrer Umgebung', 'Zwei Wochen Nachsorge‑Garantie'],
          cta: 'Projekt besprechen',
        },
        {
          name: 'Managed Support',
          tagline: 'Laufender Betrieb & Support',
          price: 'Retainer',
          features: ['Überwachte SLAs (24/7 oder Geschäftszeiten)', 'Rufbereitschaft', 'Monatlicher Health‑Report', 'Quartals‑Review'],
          cta: 'Betreuung besprechen',
        },
      ],
    },
    faq: {
      eyebrow: 'Häufig gefragt',
      title: 'Antworten, bevor wir sprechen.',
      items: [
        { q: 'Wie schnell können Sie starten?', a: 'Nach der Beauftragung beginnen wir in der Regel innerhalb von zwei Wochen. Sie haben dabei von Anfang an einen festen Ansprechpartner.' },
        { q: 'Arbeiten Sie vor Ort, remote oder hybrid?', a: 'Wir arbeiten remote, vor Ort oder in hybriden Modellen. Die meisten Projekte starten remote und werden bei wichtigen Projektphasen persönlich begleitet. Für regionale Kunden sind regelmäßige Vor-Ort-Termine jederzeit möglich.' },
        { q: 'Wem gehört Code und Dokumentation?', a: 'Ihnen. Code, Dokumentation und Konfigurationen werden vollständig in Ihren Repositories und Dokumentationssystemen bereitgestellt. Es verbleiben keine proprietären Abhängigkeiten.' },
        { q: 'Sind Sie DSGVO‑konform?', a: 'Datenschutz und Informationssicherheit sind feste Bestandteile unserer Arbeitsweise. Daten werden standardmäßig innerhalb der EU verarbeitet und unsere Sicherheitsprozesse orientieren sich an den Anforderungen der ISO 27001.' },
        { q: 'Wie sind Sie preislich aufgestellt?', a: 'Diagnosen bieten wir zum Festpreis an. Projekte werden auf Basis eines klar definierten Leistungsumfangs kalkuliert. Managed Services rechnen wir transparent als monatliche Betreuungspakete ab.' },
      ],
    },
    contact: {
      eyebrow: 'Ihr nächster Schritt',
      title: 'Lassen Sie uns über Ihre IT sprechen.',
      subtitle: 'Schildern Sie kurz Ihr Vorhaben. Ein Senior Consultant — kein Vertrieb — antwortet binnen eines Werktages.',
      form: {
        name: 'Ihr Name',
        email: 'Geschäftliche E‑Mail',
        company: 'Unternehmen',
        service: 'Wie können wir helfen?',
        message: 'Ein paar Zeilen zu Ihrem Bedarf',
        submit: 'Nachricht senden',
        sending: 'Wird gesendet…',
        success: 'Danke. Wir melden uns innerhalb eines Werktages.',
        errorRequired: 'Bitte füllen Sie die Pflichtfelder aus.',
        errorEmail: 'Bitte geben Sie eine gültige geschäftliche E‑Mail an.',
        agreement: 'Mit dem Absenden stimmen Sie unseren Datenschutzhinweisen zu. Wir geben Ihre Daten nicht weiter.',
      },
      info: [
        { label: 'E‑Mail', value: 'info@emfa-it.de',     href: 'mailto:info@emfa-it.de' },
        { label: 'Telefon', value: '+49 171 5592218',              href: 'tel:+491715592218' },
        { label: 'Büro',    value: 'Möckmühl', href: null },
        { label: 'Zeiten',  value: 'Mo–Fr · 09:00–18:00 MEZ',       href: null },
      ],
    },
    cta: {
      eyebrow: 'Ihr nächster Schritt',
      title: 'IT, die mit Ihrem Unternehmen wächst.',
      subtitle: 'Lassen Sie uns über Ihre IT sprechen.',
      primary: '30‑Minuten‑Gespräch buchen',
      secondary: 'Direkt schreiben',
    },
    footer: {
      tagline: 'IT für Unternehmen — mit System. Möckmühl.',
      columns: {
        services: 'Leistungen',
        company:  'Unternehmen',
        legal:    'Rechtliches',
      },
      links: {
        services: 'Leistungen',
        process:  'Vorgehen',
        work:     'Branchen',
        pricing:  'Modelle',
        faq:      'FAQ',
        contact:  'Kontakt',
        dataProtection: 'Datenschutz',
        imprint: 'Impressum',
      },
      rights: 'Alle Rechte vorbehalten.',
      built: 'Mit System gebaut.',
      dataProtection: 'Datenschutz',
      imprint: 'Impressum',
    },
    legal: {
      dataProtection: `Datenschutzerklärung

1. Übersicht über den Datenschutz
Die folgenden Informationen geben Ihnen einen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen.

Datenerfassung auf dieser Website
Wer ist verantwortlich für die Datenerfassung auf dieser Website?
Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber.

Wie erfassen wir Ihre Daten?
Ihre Daten werden dadurch erhoben, dass Sie uns diese mitteilen.

Wofür nutzen wir Ihre Daten?
Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Andere Daten können zur Analyse des Nutzerverhaltens verwendet werden.

Welche Rechte haben Sie bezüglich Ihrer Daten?
Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen.`,
      imprint: `Impressum

Abdullah Gül
EMFA IT Consulting
Buchenweg, 15
74219 Möckmühl

Kontakt:
E‑Mail: info@emfa-it.de

Verbraucherstreitbeilegung/Universalschlichtungsstelle
Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.`,
    },
  },
};
